<template>
  <net-item
    class="net-flow-anchor__container"
    item-class="net-flow-anchor"
    v-if="size"
    :uid="uid"
    :class="{
      '-is-selected': selected,
    }"
    :size="{
      ...size,
      width: 20,
      height: 20
    }"
    :selected="selected"
    :min-width="10"
    :min-height="10"
    @move="onMove"
    @drag-start="onDragStart"
    @drag-end="onDragEnd"
    @select="select($event)"
  >
    <div v-if="isEnd" class="net-flow-anchor__clouds"></div>
    <div v-else class="net-flow-anchor__circle"></div>
    
    <template v-if="editable && onlySelected">
      <flow-branch-popup-menu
        v-if="branchPopupmenuId != null"
        :flow-branch-id="branchPopupmenuId"
        :x="size.x + 115"
        :y="size.y + 8"
        @close="closeBranchPopupmenu"
      />
      <flow-popup-menu
        v-if="popupmenuOpen"
        :flow-id="flow.id"
        :x="size.x + 15"
        :y="size.y + 8"
        @select:branch="openBranchPopupmenu"
        @close="closePopupmenu"
      />
    </template>
  </net-item>
</template>

<script setup lang="ts">
import { computed, ref, inject, onUnmounted } from 'vue';
import { keyItemSizes } from '@editor/net/netCanvasInjectionKeys';
import { toUid } from '@/helpers/utility/Id';

import { useUndo } from '@editor/store/undo/undo.store';

import { useFlowStore } from './flows.store';
import { useSceneTree } from '../scene/sceneTree';
import { useFlowBranchStore } from './flowBranches.store';

import NetItem from '@editor/net/NetItem.vue';
import FlowPopupMenu from './FlowPopupMenu.vue';
import FlowBranchPopupMenu from './FlowBranchPopupMenu.vue';

import eventHub from '@/helpers/utility/Events';
import type { Uid } from '@/helpers/utility/Id';
import type { CanvasSelection } from '@editor/net/NetCanvasProps';
import type { SceneTreeItemSize } from '../scene/SceneTreeItem';

const props = defineProps<{
  uid: Uid<"flow">;
  selected: boolean;
  selection: CanvasSelection;
  editable: boolean;
}>();

const emit = defineEmits<{
  (e: "select", uid: { uid: Uid<"flow"> }): void;
  (e: "move", delta: { x: number; y: number; gridX: number; gridY: number }): void;
}>();

const injectedItemSizes = inject(keyItemSizes);
const sceneTree = useSceneTree();

const branchStore = useFlowBranchStore();
const flowStore = useFlowStore();

const itemSizes = computed(() => injectedItemSizes?.value || {});
const size = computed((): SceneTreeItemSize | null => itemSizes.value[props.uid] ?? null);

const id = computed(() => parseInt(props.uid.split('_')[1]));

const flow = computed(() => flowStore.items[id.value]);
const branches = computed(() => branchStore.byFlow[id.value] || []);
const sources = computed(() => branches.value.filter((b) => b.branchType == 'source'));
const sinks = computed(() => branches.value.filter((b) => b.branchType == 'sink'));
const visibleSinks = computed(() => sinks.value.filter((b) => sceneTree.elementNodes.get(toUid(b.elementType, b.elementId))?.visible));
const visibleSources = computed(() => sources.value.filter((b) => sceneTree.elementNodes.get(toUid(b.elementType, b.elementId))?.visible));
const isEnd = computed(() => visibleSinks.value.length == 0 || visibleSources.value.length == 0);

const onlySelected = computed(() => props.selected && props.selection.selected.size == 1);


function select(uid: { uid: Uid<"flow"> }) {
  emit("select", uid);
}

function onDragStart() {
  // Group the action to make sure the movement of all
  // targets can be undone at once
  useUndo().startGroup("model", "moveText");
}

function onDragEnd() {
  // Close the drag action undo group, including
  // the area update
  useUndo().endGroup("model");
}

function onMove(delta: { x: number; y: number; gridX: number; gridY: number }) {
  emit("move", delta);
}

// =====================
// POPUPMENU

const popupmenuOpen = ref(false);

function openPopupmenu() {
  console.log('open popupmenu');
  popupmenuOpen.value = true;
}

function closePopupmenu() {
  popupmenuOpen.value = false;
}

function onPopupmenuRequest(uid: Uid<"flow">) {
  if (uid === props.uid) {
    openPopupmenu();
  }
}

eventHub.on("popupmenu-request", onPopupmenuRequest);
onUnmounted(() => {
  eventHub.off("popupmenu-request", onPopupmenuRequest);
});

// =====================
// BRANCH POPUP MENU

const branchPopupmenuId = ref<number | null>(null);

function openBranchPopupmenu(branchId: number | null) {
  branchPopupmenuId.value = branchId;
}

function closeBranchPopupmenu() {
  branchPopupmenuId.value = null;
}
</script>

<style lang="scss">
.net-flow-anchor {
  position: absolute;
  background-color: black;
  pointer-events: all;

  width: 1.125rem !important;
  height: 1.125rem !important;
  margin-left: -0.5rem;
  margin-top: -0.6rem;
  border-radius: 50%;
  background-color: var(--color);

  &__container {
    position: absolute;
    
    --color: var(--color-grey-dark);

    &.-is-selected {
      --color: var(--color-primary);
    }
  }

  &.-is-end {
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }

  &__clouds {
    position: absolute;
    width: 100%;
    height: 100%;

    &::before {
      content: '';
      position: absolute;
      width: 50%;
      height: 50%;
      right: 75%;
      bottom: 0;
      border-radius: 50%;
      background-color: var(--color);
    }
    &::after {
      content: '';
      position: absolute;
      width: 75%;
      height: 75%;
      left: 50%;
      bottom: 0;
      border-radius: 50%;
      background-color: var(--color);
    }
  }

  &__circle {
    position: absolute;
    width: 100%;
    height: 110%;
    background-color: var(--color);
    border-radius: 1rem;
  }
}
</style>
